import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ExternalLink, CheckCircle2, XCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "../App";
import useUrlState from "../hooks/useUrlState";
import { isSuperAdmin } from "../hooks/useSuperAdmin";
import UserAutocomplete from "../components/UserAutocomplete";
import Hint from "../components/Hint";
import { EmptyState } from "../components/ui/empty-state";
import AvatarChip from "../components/ui/avatar-chip";
import { openAttachmentUrl } from "../lib/openAttachment";

/**
 * RecruitingPage — lean TA pipeline.
 *
 * Two tabs:
 *   Upload      — bulk table. Anyone can upload. 8 rows default, add more.
 *                 Auto-captures date + uploader. LinkedIn URL → name slug-parse.
 *   My pipeline — cards of candidates where the caller is the tagged manager.
 *                 Accept / Reject + note. LinkedIn hover-preview.
 *
 * Immutable once submitted. No duplicates (unique LinkedIn URL).
 * Private per candidate: only uploader + tagged managers + admin can see.
 */
export default function RecruitingPage() {
  const { user } = useAuth();
  const [tab, setTab] = useUrlState("tab", "upload");
  // Admins (role=admin or super-admin by email) get a third tab — "All" — that
  // shows every candidate in the system, not just ones where they're tagged as
  // reviewer. Non-admins don't see this tab; their visibility still follows
  // the uploader-or-manager rule enforced server-side.
  const isAdmin = (user?.role || "").toLowerCase() === "admin" || isSuperAdmin(user?.email);

  return (
    <div
      className="dm-page-container"
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "32px 28px 80px",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 600, color: "var(--t1)", letterSpacing: "-0.02em", margin: 0 }}>
          Recruiting
        </h1>
        <p style={{ color: "var(--t3)", fontSize: 13, marginTop: 4 }}>
          Upload candidate profiles and review ones assigned to you.
        </p>
      </div>

      {/* Tab switch */}
      <div style={{
        display: "flex",
        gap: 6,
        padding: 4,
        background: "var(--s1)",
        border: "1px solid var(--bdr)",
        borderRadius: 999,
        width: "fit-content",
        marginBottom: 28,
      }}>
        {[
          { key: "upload", label: "Upload" },
          { key: "pipeline", label: "My pipeline" },
          ...(isAdmin ? [{ key: "all", label: "All candidates" }] : []),
          { key: "performance", label: "Performance" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 20px",
              borderRadius: 999,
              border: "none",
              cursor: "pointer",
              background: tab === t.key ? "var(--s3)" : "transparent",
              color: tab === t.key ? "var(--t1)" : "var(--t3)",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              fontWeight: 500,
              transition: "all .15s",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "upload" && <UploadTab />}
      {/* "My pipeline" uses scope=visible (uploader OR manager) so TAs see
          what THEY uploaded plus anything they also manage, and managers see
          what THEY manage plus anything they also uploaded. Previously this
          was scope=mine-as-manager which hid TAs' own uploads from them —
          they'd submit candidates and have no way to track status. */}
      {tab === "pipeline" && <PipelineTab user={user} scope="visible" emptyTitle="Nothing yet" emptyMessage="Candidates you upload or are tagged to review show up here." />}
      {tab === "all" && isAdmin && <PipelineTab user={user} scope="all" emptyTitle="No candidates yet" emptyMessage="When HR uploads candidates, they show up here." />}
      {tab === "performance" && <PerformanceTab user={user} />}
    </div>
  );
}

/* ═══════════════════════ Upload Tab ═══════════════════════ */

function linkedinNameFromUrl(url) {
  if (!url) return "";
  try {
    const m = url.match(/linkedin\.com\/in\/([^/?#]+)/i);
    if (!m) return "";
    const slug = decodeURIComponent(m[1]).replace(/-\d+$/, ""); // strip trailing id
    return slug
      .split(/[-_]/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  } catch { return ""; }
}

function UploadTab() {
  // v2: single-candidate form. Was an 8-row bulk table; with R1 notes now
  // required at 20+ chars and a comm-rating gate, one-at-a-time enforces
  // thoughtful entry. The backend bulk endpoint still accepts an array, so we
  // submit a single-element array and get the existing permission + dedupe
  // behaviour for free.
  const [form, setForm] = useState({
    role: "",
    linkedin_url: "",
    name: "",
    current_salary: "",
    notice_period: "",
    cv_url: "",
    comm_rating: 0,        // 0 = unselected
    r1_done: null,         // null | true | false — must be true to submit
    r1_notes: "",
    recruiting_manager_id: "",
    secondary_manager_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [targets, setTargets] = useState(null);
  // Existing role labels in the system, sorted by frequency. Used as a
  // datalist so uploaders pick from what's already been typed instead of
  // typing a new variant. Drove role-label fragmentation: had 21 distinct
  // labels for ~7 actual roles ("Influencer Marketing" vs "Influencer Mktg",
  // "Assisstant Director" twice, etc). Trimmed + de-duped client-side, but
  // not auto-corrected — user can still type a new role if needed.
  const [roleOptions, setRoleOptions] = useState([]);
  const linkedinRef = useRef(null);

  const loadTargets = useCallback(async () => {
    try {
      const r = await api.get("/recruiting/targets/me");
      setTargets(r.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { loadTargets(); }, [loadTargets]);

  // Fetch existing role taxonomy once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/recruiting/candidates?scope=all");
        if (!alive) return;
        const counts = {};
        (r.data || []).forEach((c) => {
          const role = (c.role || "").trim();
          if (role) counts[role] = (counts[role] || 0) + 1;
        });
        const sorted = Object.entries(counts)
          .sort((a, b) => b[1] - a[1])
          .map(([r]) => r);
        setRoleOptions(sorted);
      } catch (e) { /* non-blocking */ }
    })();
    return () => { alive = false; };
  }, []);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const onLinkedIn = (url) => {
    const maybeName = linkedinNameFromUrl(url);
    patch({ linkedin_url: url, name: maybeName && !form.name ? maybeName : form.name });
  };

  // Validation summary the submit button reads from. Each failure returns a
  // short reason; the first one fires as the disabled-button title/tooltip so
  // users aren't guessing why submit is dead.
  const invalidReason = (() => {
    if (!form.role.trim()) return "Role required";
    if (!form.linkedin_url.trim()) return "LinkedIn URL required";
    if (!form.name.trim()) return "Name required";
    if (!form.cv_url.trim()) return "CV URL required";
    if (!/^https?:\/\//i.test(form.cv_url.trim())) return "CV URL must start with http(s)://";
    if (!form.comm_rating || form.comm_rating < 1 || form.comm_rating > 10) return "Communication rating required";
    if (form.r1_done !== true) return "R1 must be done — do R1 first, then upload";
    if ((form.r1_notes || "").trim().length < 20) return `R1 notes too short (${20 - (form.r1_notes || "").trim().length} more chars)`;
    if (!form.recruiting_manager_id) return "Recruiting manager required";
    return null;
  })();

  const submit = async () => {
    if (invalidReason) { toast.error(invalidReason); return; }
    setSubmitting(true);
    try {
      const payload = {
        candidates: [{
          role: form.role.trim(),
          linkedin_url: form.linkedin_url.trim(),
          name: form.name.trim(),
          current_salary: form.current_salary.trim() || null,
          notice_period: form.notice_period.trim() || null,
          cv_url: form.cv_url.trim(),
          comm_rating: form.comm_rating,
          round1_done: true,  // gate-checked above
          r1_notes: form.r1_notes.trim(),
          recruiting_manager_id: form.recruiting_manager_id,
          secondary_manager_id: form.secondary_manager_id || null,
        }],
      };
      const res = await api.post("/recruiting/candidates/bulk", payload);
      const { created, results } = res.data;
      if (created > 0) {
        toast.success("Candidate uploaded");
      } else {
        const dup = (results || []).find((r) => r.status === "duplicate");
        if (dup) toast.error("Already in the system (LinkedIn URL matches an existing candidate)");
        else toast.error("Upload didn't go through");
      }
      if (created > 0) {
        // Reset and re-focus for rapid-fire uploads.
        setForm({
          role: "", linkedin_url: "", name: "",
          current_salary: "", notice_period: "",
          cv_url: "", comm_rating: 0,
          r1_done: null, r1_notes: "",
          recruiting_manager_id: "", secondary_manager_id: "",
        });
        setTimeout(() => linkedinRef.current?.focus(), 50);
        loadTargets();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    background: "var(--s1)",
    border: "1px solid var(--bdr)",
    borderRadius: 10,
    color: "var(--t1)",
    fontFamily: "var(--font-sans)",
    fontSize: 14,
    outline: "none",
  };
  const labelStyle = { fontSize: 12, color: "var(--t3)", fontWeight: 500, marginBottom: 6, display: "block" };

  const notesLeft = Math.max(0, 20 - (form.r1_notes || "").trim().length);

  return (
    <>
      {/* Targets strip — unchanged from v1 */}
      {targets && (
        <div style={{
          display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20,
          padding: 16, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14,
        }}>
          <TargetStat label="Today" value={`${targets.today_uploads}/${targets.today_target}`}
            tone={targets.today_uploads >= targets.today_target ? "green" : targets.today_uploads >= 5 ? "amber" : "red"} />
          <TargetStat label="Week uploads" value={targets.week_uploads} tone="neutral" />
          <TargetStat label="Accepted" value={targets.week_accepted} tone="green" />
          <TargetStat label="Rejected" value={targets.week_rejected} tone="red" />
          <TargetStat label="Selection rate" value={`${targets.selection_rate}%`}
            tone={targets.selection_rate >= targets.selection_target_pct ? "green" : "amber"}
            suffix={`target ${targets.selection_target_pct}%`} />
        </div>
      )}

      {/* The form — stacked, one candidate per submit */}
      <div style={{
        display: "flex", flexDirection: "column", gap: 18,
        padding: 24,
        background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 14,
        maxWidth: 720,
      }}>
        <div>
          <label style={labelStyle}>LinkedIn URL *</label>
          <input
            ref={linkedinRef}
            autoFocus
            value={form.linkedin_url}
            onChange={(e) => onLinkedIn(e.target.value)}
            placeholder="https://linkedin.com/in/…"
            style={inputStyle}
          />
          <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 4 }}>Name auto-fills from the URL slug. Edit below if needed.</div>
        </div>

        <div className="dm-recruit-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Name *</label>
            <input value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Full name" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Role *</label>
            <input
              value={form.role}
              onChange={(e) => patch({ role: e.target.value })}
              placeholder="Pick existing or type new"
              list="dm-role-options"
              style={inputStyle}
            />
            {roleOptions.length > 0 && (
              <datalist id="dm-role-options">
                {roleOptions.map((r) => <option key={r} value={r} />)}
              </datalist>
            )}
          </div>
        </div>

        <div className="dm-recruit-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Current salary <span style={{ color: "var(--t4)" }}>(optional)</span></label>
            <input value={form.current_salary} onChange={(e) => patch({ current_salary: e.target.value })} placeholder="e.g. 12L" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Notice period <span style={{ color: "var(--t4)" }}>(optional)</span></label>
            <input value={form.notice_period} onChange={(e) => patch({ notice_period: e.target.value })} placeholder="30 days" style={inputStyle} />
          </div>
        </div>

        <div>
          <label style={labelStyle}>CV URL *</label>
          <input value={form.cv_url} onChange={(e) => patch({ cv_url: e.target.value })}
            placeholder="Paste CV link — Drive, Dropbox, WhatsApp, LinkedIn share…" style={inputStyle} />
          <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 4 }}>
            Paste any public-or-shared URL. We don't host the CV — we just link to yours.
          </div>
        </div>

        {/* Communication rating 1..10 — 10 pill buttons, click to select */}
        <div>
          <label style={labelStyle}>
            Communication <span style={{ color: "var(--t4)" }}>(1–10)</span> *
            <Hint id="recruit.comm_rating">
              Your honest read from the R1 call. Drives the first cut — candidates rated below 5 rarely move forward, 8+ get prioritised.
            </Hint>
          </label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => {
              const active = form.comm_rating === n;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => patch({ comm_rating: n })}
                  title={n === 1 ? "Poor" : n === 10 ? "Exceptional" : ""}
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    border: "1px solid " + (active ? "#e31937" : "var(--bdr)"),
                    background: active ? "#e31937" : "var(--s2)",
                    color: active ? "#fff" : "var(--t2)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {n}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 6 }}>
            1 = Poor · 5 = Acceptable · 10 = Exceptional. Your honest read from the R1 call.
          </div>
        </div>

        {/* R1 Yes/No toggle */}
        <div>
          <label style={labelStyle}>
            Have you completed R1? *
            <Hint id="recruit.r1_done">
              Round 1 happens off-platform first — Zoom or in-person. Mark Yes only after you've actually had the call. Notes below capture what you learned.
            </Hint>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            {[{k:true,label:"Yes, R1 done"},{k:false,label:"No — not yet"}].map((opt) => {
              const active = form.r1_done === opt.k;
              return (
                <button
                  key={String(opt.k)}
                  type="button"
                  onClick={() => patch({ r1_done: opt.k })}
                  title={opt.k ? "" : "Do R1 first, then come back. R1 is required before uploading."}
                  style={{
                    padding: "9px 16px", borderRadius: 10,
                    border: "1px solid " + (active ? (opt.k ? "#22c55e" : "#94a3b8") : "var(--bdr)"),
                    background: active ? (opt.k ? "rgba(34,197,94,.12)" : "rgba(148,163,184,.12)") : "var(--s2)",
                    color: active ? (opt.k ? "#22c55e" : "#94a3b8") : "var(--t2)",
                    fontSize: 13, fontWeight: 500, cursor: "pointer",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {form.r1_done === false && (
            <div style={{ fontSize: 12, color: "var(--a)", marginTop: 8 }}>
              R1 is required before uploading. Complete R1 with the candidate, then come back with notes.
            </div>
          )}
        </div>

        {/* R1 notes — only enabled when R1 = Yes */}
        <div>
          <label style={labelStyle}>
            R1 notes *
            {form.r1_done === true && (
              <span style={{ marginLeft: 8, color: notesLeft > 0 ? "var(--a)" : "var(--g)", fontWeight: 400 }}>
                {notesLeft > 0 ? `${notesLeft} more characters needed` : "✓ long enough"}
              </span>
            )}
          </label>
          <textarea
            value={form.r1_notes}
            onChange={(e) => patch({ r1_notes: e.target.value })}
            placeholder={form.r1_done === true
              ? "What stood out in R1 — strengths, concerns, fit for the role, salary expectations, etc. Minimum 20 characters."
              : "Say Yes to R1 above first"}
            disabled={form.r1_done !== true}
            rows={4}
            style={{ ...inputStyle, resize: "vertical", minHeight: 90, opacity: form.r1_done === true ? 1 : 0.5 }}
          />
        </div>

        <div className="dm-recruit-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Recruiting manager *</label>
            <UserAutocomplete value={form.recruiting_manager_id} onChange={(v) => patch({ recruiting_manager_id: v })} />
          </div>
          <div>
            <label style={labelStyle}>Secondary manager <span style={{ color: "var(--t4)" }}>(optional)</span></label>
            <UserAutocomplete value={form.secondary_manager_id} onChange={(v) => patch({ secondary_manager_id: v })} />
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 6 }}>
          <span style={{ flex: 1, fontSize: 12, color: "var(--t3)" }}>
            {invalidReason ? invalidReason : "All set — submit to add this candidate"}
          </span>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !!invalidReason}
            title={invalidReason || ""}
            style={{
              padding: "10px 24px",
              background: submitting || invalidReason ? "var(--s3)" : "#e31937",
              border: "1px solid " + (submitting || invalidReason ? "var(--bdr)" : "#e31937"),
              borderRadius: 999,
              color: submitting || invalidReason ? "var(--t3)" : "#fff",
              fontSize: 14, fontWeight: 600,
              cursor: submitting || invalidReason ? "not-allowed" : "pointer",
            }}
          >
            {submitting ? "Uploading…" : "Upload candidate"}
          </button>
        </div>
      </div>

      <div style={{
        marginTop: 20, padding: 14,
        background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12,
        fontSize: 12, color: "var(--t3)", lineHeight: 1.5, maxWidth: 720,
      }}>
        <strong style={{ color: "var(--t2)" }}>How this works:</strong> candidates are locked once submitted — no edits, no deletes through this form. Duplicate LinkedIn URLs are silently rejected. Date and your identity as uploader are captured automatically. Do R1 before uploading — no exceptions.
      </div>
    </>
  );
}


function TargetStat({ label, value, tone, suffix }) {
  const tones = {
    red: { color: "var(--r)", bg: "rgba(239,68,68,.1)" },
    amber: { color: "var(--a)", bg: "rgba(245,158,11,.1)" },
    green: { color: "var(--g)", bg: "rgba(34,197,94,.1)" },
    neutral: { color: "var(--t2)", bg: "var(--s2)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{
      background: t.bg,
      border: "1px solid var(--bdr)",
      borderRadius: 12,
      padding: "10px 14px",
      minWidth: 110,
    }}>
      <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color: t.color, letterSpacing: "-0.01em", marginTop: 2 }}>{value}</div>
      {suffix && <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 2 }}>{suffix}</div>}
    </div>
  );
}


/* ═══════════════════════ My pipeline tab ═══════════════════════ */

/* ═══════════════════════ My pipeline tab ═══════════════════════ */

/**
 * Pipeline view — v2: rich inline cards, no modal.
 * Previously each card was a thin summary and every action required opening
 * a modal. Now every action (LinkedIn, Open CV, Accept/Reject/Hire/Didn't
 * pass, decision note, edit comm/R1) lives on the card itself. Two benefits:
 *   - No click-to-modal friction on the common action.
 *   - Info density — manager scanning a grid of 20 candidates sees all the
 *     signals at once instead of opening each one.
 *
 * Search + role chips + stale-chip land in the next commit (separate concern).
 */
function PipelineTab({ user, scope = "visible", emptyTitle, emptyMessage }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useUrlState("status", "pending");
  // Search: free-text across name / role / LinkedIn URL / R1 notes / decision
  // note / uploader name. Debounced via useMemo so the DOM filter is cheap.
  const [searchRaw, setSearchRaw] = useUrlState("q", "");
  // Role chips: multi-select (AND). URL uses comma-joined list.
  const [roleParam, setRoleParam] = useUrlState("role", "");
  const roles = useMemo(
    () => roleParam.split(",").map((r) => r.trim()).filter(Boolean),
    [roleParam]
  );
  const setRoles = (next) => setRoleParam(next.join(","));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get(`/recruiting/candidates?scope=${scope}${statusFilter !== "all" ? `&status=${statusFilter}` : ""}`);
      setCandidates(r.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load pipeline");
    } finally { setLoading(false); }
  }, [statusFilter, scope]);
  useEffect(() => { load(); }, [load]);

  // Applied optimistically by the card; parent keeps the master list in sync.
  const replaceCandidate = (updated) => {
    setCandidates((prev) => prev.map((c) => c.candidate_id === updated.candidate_id ? updated : c));
  };

  // Distinct roles in the current (unfiltered) candidate set, sorted by count
  // desc. Drives the role chip row — admins looking at hundreds of candidates
  // get a quick "what roles are we hiring for" view at a glance.
  const roleCounts = useMemo(() => {
    const m = new Map();
    for (const c of candidates) {
      const r = (c.role || "").trim();
      if (!r) continue;
      m.set(r, (m.get(r) || 0) + 1);
    }
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([role, count]) => ({ role, count }));
  }, [candidates]);

  const toggleRole = (role) => {
    if (roles.includes(role)) setRoles(roles.filter((r) => r !== role));
    else setRoles([...roles, role]);
  };

  // Client-side filter after load. Candidates is already scoped + status-filtered
  // by the backend; we layer search + role narrowing on top.
  const filtered = useMemo(() => {
    const q = (searchRaw || "").trim().toLowerCase();
    return candidates.filter((c) => {
      // Role AND filter — if any chip is active, candidate must match ONE of
      // them (role is a single field so AND across multiple roles would be
      // empty; semantically this is OR within role chips, AND with search).
      if (roles.length && !roles.includes(c.role)) return false;
      if (q) {
        const hay = `${c.name || ""} ${c.role || ""} ${c.linkedin_url || ""} ${c.r1_notes || ""} ${c.decision_note || ""} ${c.uploaded_by_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [candidates, searchRaw, roles]);

  const hasFilter = !!(searchRaw.trim() || roles.length > 0);
  const clearFilters = () => { setSearchRaw(""); setRoles([]); };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: "var(--t3)" }}>Loading…</div>;
  }

  return (
    <>
      {/* Search box — free-text across most of the candidate record */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={searchRaw}
          onChange={(e) => setSearchRaw(e.target.value)}
          placeholder="Search name, role, LinkedIn, R1 notes…"
          style={{
            width: "100%",
            maxWidth: 420,
            padding: "9px 14px",
            background: "var(--s1)",
            border: "1px solid var(--bdr)",
            borderRadius: 999,
            color: "var(--t1)",
            fontFamily: "var(--font-sans)",
            fontSize: 13,
            outline: "none",
          }}
        />
      </div>

      {/* Status filter pills */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {["pending", "accepted", "hired", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              border: "1px solid var(--bdr)",
              background: statusFilter === s ? "var(--s3)" : "var(--s1)",
              color: statusFilter === s ? "var(--t1)" : "var(--t3)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Role chips — one chip per distinct role in the loaded set, ordered by
          count. Multi-select (OR within role, AND with search). Hidden when
          only one role exists (chip row would be useless). */}
      {roleCounts.length > 1 && (
        <div style={{
          display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap",
          alignItems: "center",
          paddingBottom: 4,
        }}>
          <span style={{ fontSize: 11, color: "var(--t3)", marginRight: 4 }}>Roles:</span>
          {roleCounts.map(({ role, count }) => {
            const active = roles.includes(role);
            return (
              <button
                key={role}
                onClick={() => toggleRole(role)}
                style={{
                  padding: "5px 11px",
                  borderRadius: 999,
                  border: "1px solid " + (active ? "var(--t1)" : "var(--bdr)"),
                  background: active ? "var(--t1)" : "var(--s1)",
                  color: active ? "var(--bg)" : "var(--t2)",
                  fontSize: 11,
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                {role}
                <span style={{ fontSize: 10, opacity: active ? 0.7 : 0.5 }}>{count}</span>
              </button>
            );
          })}
          {hasFilter && (
            <>
              <span style={{ flex: 1 }} />
              <button
                onClick={clearFilters}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--t3)",
                  fontSize: 11,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          title={hasFilter ? "Nothing matches" : (statusFilter === "pending" ? (emptyTitle || "Inbox zero") : "No candidates")}
          message={hasFilter
            ? "Try a different search or clear the filter."
            : statusFilter === "pending"
              ? (emptyMessage || "Nothing waiting for your review.")
              : `No ${statusFilter === "all" ? "" : statusFilter} candidates${scope === "all" ? "" : " assigned to you"}.`}
        />
      ) : (
        <div className="dm-recruit-pipeline-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 14 }}>
          {filtered.map((c) => (
            <CandidateCard
              key={c.candidate_id}
              candidate={c}
              onUpdate={replaceCandidate}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* ═══════════════════════ Candidate card (rich, inline, no modal) ═══════════════════════ */

/**
 * Full-surface card. Holds its own local state for:
 *   - the decision note being typed
 *   - the "edit comm/R1" mode (toggled by a small pencil)
 *   - expanded-notes state (R1 notes clamp to 3 lines; click to expand)
 * Actions hit the server with optimistic updates; failures roll back via
 * the parent's `onUpdate` callback and a toast.
 */
function CandidateCard({ candidate, onUpdate }) {
  const c = candidate;
  const [note, setNote] = useState(c.decision_note || "");
  const [submitting, setSubmitting] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editComm, setEditComm] = useState(c.comm_rating || 0);
  const [editR1Notes, setEditR1Notes] = useState(c.r1_notes || "");
  const [editCvUrl, setEditCvUrl] = useState(c.cv_url || "");
  const [savingEdit, setSavingEdit] = useState(false);

  // Decision action (Accept, Reject, Hire, Didn't pass). Unified so the 4
  // buttons share the same optimistic + rollback behaviour.
  const decide = async (status) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/recruiting/candidates/${c.candidate_id}/decision`, {
        status,
        note: (note || "").trim() || null,
      });
      onUpdate(r.data);
      const labels = { accepted: "Accepted", rejected: "Rejected", hired: "Hired", pending: "Reopened" };
      toast.success(labels[status] || "Updated");
      setNote("");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Couldn't update");
    } finally {
      setSubmitting(false);
    }
  };

  // Open-in-new-tab for CV. Prefers new cv_url; falls back to legacy cv_id
  // (stored bytes) via the authed blob helper so old candidates still work.
  const openCv = async () => {
    if (c.cv_url) {
      window.open(c.cv_url, "_blank", "noopener,noreferrer");
      return;
    }
    if (c.cv_id) {
      await openAttachmentUrl(`/api/recruiting/cv/${c.cv_id}`, c.cv_filename);
    }
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {};
      if (editComm && editComm !== c.comm_rating) payload.comm_rating = editComm;
      if (editR1Notes.trim() !== (c.r1_notes || "").trim()) payload.r1_notes = editR1Notes.trim();
      if (editCvUrl.trim() !== (c.cv_url || "").trim()) payload.cv_url = editCvUrl.trim();
      if (Object.keys(payload).length === 0) { setEditing(false); return; }
      const r = await api.patch(`/recruiting/candidates/${c.candidate_id}`, payload);
      onUpdate(r.data);
      toast.success("Updated");
      setEditing(false);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Couldn't update");
    } finally {
      setSavingEdit(false);
    }
  };

  // Stale pending chip — added in the next commit. Placeholder here so the
  // layout doesn't shift when we wire it up.
  const ageDays = c.uploaded_at ? Math.floor((Date.now() - new Date(c.uploaded_at).getTime()) / 86400000) : 0;
  const isStale = c.status === "pending" && ageDays > 7;

  // Status chip mapping. Adds "hired" which wasn't in the old status chip.
  const statusMap = {
    pending: { label: "Pending", color: "#64748b", bg: "rgba(100,116,139,.15)" },
    accepted: { label: "Accepted", color: "#3b82f6", bg: "rgba(59,130,246,.15)" },
    hired: { label: "Hired", color: "#22c55e", bg: "rgba(34,197,94,.15)" },
    rejected: { label: "Rejected", color: "#94a3b8", bg: "rgba(148,163,184,.12)" },
  };
  const sc = statusMap[c.status] || statusMap.pending;

  return (
    <div
      style={{
        background: "var(--s1)",
        border: "1px solid var(--bdr)",
        borderRadius: 14,
        padding: 16,
        fontFamily: "var(--font-sans)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      {/* Header: avatar + name + role + status chip (+ stale chip) */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <AvatarChip name={c.name} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--t1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {c.name}
          </div>
          <div style={{ fontSize: 12, color: "var(--t3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {c.role}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
          <span style={{ padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, color: sc.color, background: sc.bg }}>
            {sc.label}
          </span>
          {isStale && (
            <span style={{ padding: "3px 8px", borderRadius: 999, fontSize: 10, fontWeight: 500, color: ageDays > 14 ? "#ef4444" : "#f59e0b", background: ageDays > 14 ? "rgba(239,68,68,.1)" : "rgba(245,158,11,.1)" }}>
              Waiting {ageDays}d
            </span>
          )}
        </div>
      </div>

      {/* Meta row: salary · notice · comm rating */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 12, color: "var(--t3)", alignItems: "center" }}>
        {c.current_salary && <span>{c.current_salary}</span>}
        {c.current_salary && (c.notice_period || c.comm_rating != null) && <span style={{ color: "var(--t4)" }}>·</span>}
        {c.notice_period && <span>{c.notice_period}</span>}
        {c.notice_period && c.comm_rating != null && <span style={{ color: "var(--t4)" }}>·</span>}
        {c.comm_rating != null && (
          <span style={{ color: "var(--t2)" }}>
            Comm <strong style={{ color: "var(--t1)" }}>{c.comm_rating}</strong>/10
          </span>
        )}
      </div>

      {/* R1 notes — clamp to 3 lines, click to expand */}
      {c.r1_notes ? (
        <div>
          <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>R1 notes</div>
          <div
            style={{
              fontSize: 13,
              color: "var(--t2)",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: notesExpanded ? "unset" : 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={() => setNotesExpanded(!notesExpanded)}
            title={notesExpanded ? "Click to collapse" : "Click to expand"}
          >
            {c.r1_notes}
          </div>
        </div>
      ) : c.round1_done ? (
        <div style={{ fontSize: 12, color: "var(--t3)", fontStyle: "italic" }}>R1 done — no notes</div>
      ) : (
        <div style={{ fontSize: 12, color: "var(--a)", fontStyle: "italic" }}>R1 pending</div>
      )}

      {/* Link buttons row */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {c.linkedin_url && (
          <a
            href={c.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px",
              background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 999,
              color: "var(--t2)", fontSize: 12, fontWeight: 500, textDecoration: "none",
            }}
          >
            <ExternalLink size={12} /> LinkedIn
          </a>
        )}
        {(c.cv_url || c.cv_id) && (
          <button
            type="button"
            onClick={openCv}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 12px",
              background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 999,
              color: "var(--t2)", fontSize: 12, fontWeight: 500, cursor: "pointer",
            }}
          >
            <Download size={12} /> Open CV
          </button>
        )}
        <span style={{ flex: 1 }} />
        {(c.status === "pending" || c.status === "accepted") && !editing && (
          <button
            type="button"
            onClick={() => {
              setEditing(true);
              setEditComm(c.comm_rating || 0);
              setEditR1Notes(c.r1_notes || "");
              setEditCvUrl(c.cv_url || "");
            }}
            title="Edit comm rating / R1 notes / CV URL"
            style={{ background: "transparent", border: "none", color: "var(--t3)", cursor: "pointer", padding: 4, display: "flex" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
          </button>
        )}
      </div>

      {/* Inline edit panel (pencil icon opens this) */}
      {editing && (
        <div style={{
          padding: 12,
          background: "var(--s2)",
          border: "1px solid var(--bdr)",
          borderRadius: 10,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 4 }}>Communication (1–10)</div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                <button key={n} type="button" onClick={() => setEditComm(n)}
                  style={{
                    width: 26, height: 26, borderRadius: 6,
                    border: "1px solid " + (editComm === n ? "#e31937" : "var(--bdr)"),
                    background: editComm === n ? "#e31937" : "var(--s1)",
                    color: editComm === n ? "#fff" : "var(--t2)",
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>{n}</button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 4 }}>R1 notes</div>
            <textarea value={editR1Notes} onChange={(e) => setEditR1Notes(e.target.value)}
              rows={3}
              style={{
                width: "100%", padding: "8px 10px",
                background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 8,
                color: "var(--t1)", fontSize: 13, fontFamily: "var(--font-sans)", outline: "none",
                resize: "vertical",
              }}
            />
          </div>
          <div>
            <div style={{ fontSize: 11, color: "var(--t3)", marginBottom: 4 }}>CV URL</div>
            <input value={editCvUrl} onChange={(e) => setEditCvUrl(e.target.value)}
              placeholder="https://…"
              style={{
                width: "100%", padding: "8px 10px",
                background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 8,
                color: "var(--t1)", fontSize: 13, fontFamily: "var(--font-sans)", outline: "none",
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={() => setEditing(false)}
              style={{ padding: "6px 14px", background: "transparent", border: "1px solid var(--bdr)", borderRadius: 999, color: "var(--t2)", fontSize: 12, cursor: "pointer" }}
            >Cancel</button>
            <button type="button" onClick={saveEdit} disabled={savingEdit}
              style={{ padding: "6px 14px", background: "var(--t1)", border: "1px solid var(--t1)", borderRadius: 999, color: "var(--bg)", fontSize: 12, fontWeight: 600, cursor: savingEdit ? "wait" : "pointer" }}
            >{savingEdit ? "Saving…" : "Save"}</button>
          </div>
        </div>
      )}

      {/* Decision zone — only on pending + accepted. Hired/rejected are terminal. */}
      {(c.status === "pending" || c.status === "accepted") && !editing && (
        <>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={c.status === "pending" ? "Optional note for this decision" : "Note for post-interview call"}
            style={{
              width: "100%", padding: "8px 12px",
              background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10,
              color: "var(--t1)", fontSize: 12, fontFamily: "var(--font-sans)", outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            {c.status === "pending" && (
              <>
                <button
                  type="button"
                  onClick={() => decide("rejected")}
                  disabled={submitting}
                  style={{ flex: 1, padding: "9px 14px", border: "1px solid rgba(239,68,68,.3)", background: "rgba(239,68,68,.08)", color: "#ef4444", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                >
                  <XCircle size={14} /> Reject
                </button>
                <button
                  type="button"
                  onClick={() => decide("accepted")}
                  disabled={submitting}
                  style={{ flex: 1, padding: "9px 14px", background: "#22c55e", border: "1px solid #22c55e", color: "#0b0b0d", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                >
                  <CheckCircle2 size={14} /> Accept
                </button>
              </>
            )}
            {c.status === "accepted" && (
              <>
                <button
                  type="button"
                  onClick={() => decide("rejected")}
                  disabled={submitting}
                  title="Did not pass the interview"
                  style={{ flex: 1, padding: "9px 14px", border: "1px solid rgba(239,68,68,.3)", background: "rgba(239,68,68,.08)", color: "#ef4444", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                >
                  <XCircle size={14} /> Didn't pass
                </button>
                <button
                  type="button"
                  onClick={() => decide("hired")}
                  disabled={submitting}
                  title="Candidate joined — closes the loop for performance metrics"
                  style={{ flex: 1, padding: "9px 14px", background: "#22c55e", border: "1px solid #22c55e", color: "#0b0b0d", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: submitting ? "wait" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                >
                  <CheckCircle2 size={14} /> Mark as hired
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* Terminal-state panel: show the recorded decision note + date */}
      {(c.status === "hired" || c.status === "rejected") && (
        <div style={{
          fontSize: 12, color: "var(--t3)",
          padding: 10,
          background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10,
          lineHeight: 1.5,
        }}>
          {c.decision_note && <div style={{ color: "var(--t2)", marginBottom: 4 }}>"{c.decision_note}"</div>}
          <div>
            {c.status === "hired" && c.hired_at
              ? `Hired on ${new Date(c.hired_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
              : c.decision_at
                ? `${c.status === "hired" ? "Hired" : "Rejected"} on ${new Date(c.decision_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                : ""
            }
          </div>
        </div>
      )}

      {/* Footer attribution */}
      <div style={{ fontSize: 11, color: "var(--t4, #3e3e3e)", marginTop: "auto" }}>
        Uploaded by {c.uploaded_by_name || "—"} · {new Date(c.uploaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
      </div>
    </div>
  );
}

/* ═══════════════════════ Performance tab ═══════════════════════ */

/**
 * Per-TA performance dashboard. Visible to everyone — transparency > secrecy
 * for internal org metrics. Renders a table ranked by uploads with each TA's
 * upload count, interview line-ups (accepted), rejections, hires, and two
 * conversion rates.
 *
 * Window selector lives at the top (7d / 30d / 90d / All time). The viewer's
 * own row is highlighted so they can find themselves fast in a long list.
 */
function PerformanceTab({ user }) {
  const [window, setWindow] = useUrlState("win", "30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedUid, setExpandedUid] = useState(null);
  const myUid = user?.user_id || user?.id;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const r = await api.get(`/recruiting/performance?window=${window}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error("Couldn't load performance");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [window]);

  const WINDOWS = [
    { key: "7d", label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "90d", label: "90 days" },
    { key: "all", label: "All time" },
  ];

  if (loading) {
    return <div style={{ textAlign: "center", padding: 60, color: "var(--t3)" }}>Loading…</div>;
  }
  if (!data) return null;

  const { rows, totals } = data;

  return (
    <>
      {/* Window selector */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setWindow(w.key)}
            style={{
              padding: "6px 14px",
              borderRadius: 999,
              border: "1px solid var(--bdr)",
              background: window === w.key ? "var(--s3)" : "var(--s1)",
              color: window === w.key ? "var(--t1)" : "var(--t3)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            {w.label}
          </button>
        ))}
      </div>

      {/* Totals strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          marginBottom: 24,
        }}
      >
        <TotalCell label="Uploaded" value={totals.uploaded} sub={`${totals.uploaded_today} today`} />
        <TotalCell label="Line-ups" value={totals.accepted} sub="accepted + hired" />
        <TotalCell label="Hired" value={totals.hired} accent="#22c55e" />
        <TotalCell label="Pending" value={totals.pending} sub="awaiting decision" />
        <TotalCell label="Active TAs" value={totals.active_tas} />
      </div>

      {/* Per-TA table */}
      {rows.length === 0 ? (
        <EmptyState title="No activity in this window" message="Try a longer window, or start uploading candidates." />
      ) : (
        <div
          className="dm-recruit-perf-table"
          style={{
            background: "var(--s1)",
            border: "1px solid var(--bdr)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1.6fr 90px .7fr .6fr .7fr .7fr .8fr .8fr 18px",
              gap: 8,
              padding: "10px 16px",
              borderBottom: "1px solid var(--bdr)",
              fontSize: 11,
              color: "var(--t3)",
              fontWeight: 600,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              background: "var(--s2)",
              minWidth: 640,
            }}
          >
            <div>Recruiter</div>
            <div style={{ textAlign: "left" }}>Trend{data.series_days ? ` · ${data.series_days}d` : ""}</div>
            <div style={{ textAlign: "right" }}>Uploaded</div>
            <div style={{ textAlign: "right" }}>Today</div>
            <div style={{ textAlign: "right" }}>Line-ups</div>
            <div style={{ textAlign: "right" }}>Hired</div>
            <div style={{ textAlign: "right" }}>Accept %</div>
            <div style={{ textAlign: "right" }}>Hire %</div>
            <div></div>
          </div>
          {/* Rows */}
          {rows.map((r) => {
            const isMe = r.user_id === myUid;
            const isExpanded = expandedUid === r.user_id;
            const series = r.daily_uploads_series || [];
            return (
              <React.Fragment key={r.user_id}>
                <div
                  onClick={() => setExpandedUid(isExpanded ? null : r.user_id)}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.6fr 90px .7fr .6fr .7fr .7fr .8fr .8fr 18px",
                    gap: 8,
                    padding: "12px 16px",
                    borderBottom: "1px solid var(--bdr)",
                    background: isMe ? "rgba(96,165,250,.06)" : "transparent",
                    alignItems: "center",
                    minWidth: 640,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <AvatarChip name={r.name} size={24} />
                    <span
                      style={{
                        fontSize: 14,
                        color: "var(--t1)",
                        fontWeight: isMe ? 600 : 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {r.name}{isMe ? " · you" : ""}
                    </span>
                  </div>
                  <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
                    <Sparkline values={series} title={`${series.reduce((a,b)=>a+b,0)} uploads · last ${series.length} days`} />
                  </div>
                  <div style={{ textAlign: "right", fontSize: 14, color: "var(--t1)", fontWeight: 600 }}>{r.uploaded}</div>
                  <div style={{ textAlign: "right", fontSize: 14, color: r.uploaded_today > 0 ? "var(--t1)" : "var(--t3)" }}>{r.uploaded_today}</div>
                  <div style={{ textAlign: "right", fontSize: 14, color: "var(--t1)" }}>{r.accepted}</div>
                  <div style={{ textAlign: "right", fontSize: 14, color: r.hired > 0 ? "#22c55e" : "var(--t3)", fontWeight: r.hired > 0 ? 600 : 400 }}>{r.hired}</div>
                  <div style={{ textAlign: "right", fontSize: 13, color: "var(--t2)" }}>
                    {r.acceptance_rate == null ? "—" : `${Math.round(r.acceptance_rate * 100)}%`}
                  </div>
                  <div style={{ textAlign: "right", fontSize: 13, color: "var(--t2)" }}>
                    {r.hire_rate == null ? "—" : `${Math.round(r.hire_rate * 100)}%`}
                  </div>
                  <div style={{
                    color: "var(--t3)",
                    fontSize: 11,
                    transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                    transition: "transform .15s",
                    textAlign: "center",
                  }}>▶</div>
                </div>
                {isExpanded && (
                  <DailyBreakdown rows={r.daily || []} totalDays={data.series_days} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Legend / how-to-read */}
      <div
        style={{
          marginTop: 16,
          padding: 14,
          background: "var(--s1)",
          border: "1px solid var(--bdr)",
          borderRadius: 10,
          fontSize: 12,
          color: "var(--t3)",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "var(--t2)" }}>How to read this:</strong><br/>
        <strong>Uploaded</strong> — candidates the recruiter added to the system.<br/>
        <strong>Line-ups</strong> — candidates moved to interview stage (accepted OR hired).<br/>
        <strong>Hired</strong> — candidates who completed the loop and joined the team.<br/>
        <strong>Accept %</strong> — line-ups ÷ decided. How strong is their sourcing.<br/>
        <strong>Hire %</strong> — hires ÷ line-ups. How well candidates convert after interviews.
      </div>
    </>
  );
}

function Sparkline({ values, color = "#60a5fa", title }) {
  if (!values || values.length === 0) {
    return <span style={{ fontSize: 12, color: "var(--t3)" }}>—</span>;
  }
  const max = Math.max(1, ...values);
  const w = 84, h = 22, gap = 1;
  const barW = Math.max(1, (w - gap * (values.length - 1)) / values.length);
  return (
    <svg width={w} height={h} style={{ display: "block" }} aria-label={title}>
      <title>{title}</title>
      {values.map((v, i) => {
        const bh = v === 0 ? 2 : Math.max(2, (v / max) * h);
        return (
          <rect
            key={i}
            x={i * (barW + gap)}
            y={h - bh}
            width={barW}
            height={bh}
            fill={v === 0 ? "var(--t4, #888)" : color}
            opacity={v === 0 ? 0.35 : 1}
            rx={1}
          />
        );
      })}
    </svg>
  );
}

function DailyBreakdown({ rows, totalDays }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{
        padding: "14px 16px 18px 56px",
        borderBottom: "1px solid var(--bdr)",
        background: "var(--s2)",
        fontSize: 12,
        color: "var(--t3)",
      }}>
        No activity in the last {totalDays} days.
      </div>
    );
  }
  return (
    <div style={{
      padding: "10px 16px 14px 56px",
      borderBottom: "1px solid var(--bdr)",
      background: "var(--s2)",
    }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "120px 80px 80px 80px",
        gap: 8,
        padding: "6px 0",
        fontSize: 10,
        color: "var(--t3)",
        fontWeight: 600,
        letterSpacing: ".04em",
        textTransform: "uppercase",
        borderBottom: "1px solid var(--bdr)",
      }}>
        <div>Date</div>
        <div style={{ textAlign: "right" }}>Uploaded</div>
        <div style={{ textAlign: "right" }}>Line-ups</div>
        <div style={{ textAlign: "right" }}>Hired</div>
      </div>
      {rows.map((d) => (
        <div key={d.date} style={{
          display: "grid",
          gridTemplateColumns: "120px 80px 80px 80px",
          gap: 8,
          padding: "8px 0",
          fontSize: 13,
          color: "var(--t2)",
          borderBottom: "1px dashed var(--bdr)",
        }}>
          <div style={{ color: "var(--t1)", fontWeight: 500 }}>
            {formatBreakdownDate(d.date)}
          </div>
          <div style={{ textAlign: "right", color: "var(--t1)", fontWeight: 600 }}>{d.uploaded}</div>
          <div style={{ textAlign: "right" }}>{d.accepted}</div>
          <div style={{ textAlign: "right", color: d.hired > 0 ? "#22c55e" : "var(--t3)", fontWeight: d.hired > 0 ? 600 : 400 }}>{d.hired}</div>
        </div>
      ))}
    </div>
  );
}

function formatBreakdownDate(ymd) {
  // ymd like "2026-05-03". Return "Sat, 3 May".
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  const wk = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][dt.getUTCDay()];
  const mn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][m - 1];
  return `${wk}, ${d} ${mn}`;
}

function TotalCell({ label, value, sub, accent }) {
  return (
    <div
      style={{
        background: "var(--s1)",
        border: "1px solid var(--bdr)",
        borderRadius: 12,
        padding: "14px 16px",
      }}
    >
      <div style={{ fontSize: 11, color: "var(--t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: accent || "var(--t1)", lineHeight: 1, letterSpacing: "-0.01em" }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}
